import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { queryClient } from "@/lib/queryClient";
import { clearEnteredInterface } from "@/lib/entered";

interface Profile {
  id: string;
  displayName: string;
  isSaved: boolean;
  savedAt?: string;
  createdAt: string;
  subscriptionEmail?: string;
  isSubscribed?: boolean;
  promoExpiresAt?: string;
  expiresAt?: string | null;
}

interface ProfileContextType {
  profile: Profile | null;
  isLoading: boolean;
  isReconnecting: boolean;
  reconnectFailed: boolean;
  dismissReconnectFailed: () => void;
  setProfile: (profile: Profile | null) => void;
  refreshProfile: () => Promise<void>;
  signOut: () => void;
}

const ProfileContext = createContext<ProfileContextType | null>(null);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfileState] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectFailed, setReconnectFailed] = useState(false);

  useEffect(() => {
    // Watchdog: never let the app hang on the "Loading..." screen. If the
    // profile bootstrap fetches stall (common on flaky mobile networks), force
    // isLoading=false after a timeout so the app renders in guest mode instead
    // of showing an infinite spinner.
    let settled = false;
    let resolved = false; // a real (signed-in) profile was applied
    let cleanedUp = false;
    let watchdogFiredWhileUnresolved = false;
    const controller = new AbortController();
    const finish = () => {
      if (settled) return;
      settled = true;
      setIsLoading(false);
    };
    const watchdog = setTimeout(() => {
      // Stop any stalled bootstrap fetches so their late results can't apply
      // after we've already fallen back to guest mode (avoids UI flicker).
      controller.abort();
      if (!resolved) watchdogFiredWhileUnresolved = true;
      finish();
    }, 8000);

    // Runs the full bootstrap sequence against the given signal. Returns true if
    // a profile was successfully applied, false otherwise.
    async function bootstrapProfile(signal: AbortSignal): Promise<boolean> {
      const apply = (data: Profile) => {
        if (cleanedUp || signal.aborted) return false;
        resolved = true;
        setProfileState(data);
        return true;
      };

      try {
        const authRes = await fetch("/api/auth/user", { credentials: "include", signal });
        if (authRes.ok) {
          const authUser = await authRes.json();
          const profileRes = await fetch(`/api/profile/${authUser.id}`, { credentials: "include", signal }).catch((e) => { console.warn("[Profile] Fetch failed:", e); return null; });
          if (profileRes?.ok) {
            const data = await profileRes.json();
            localStorage.setItem("memnora-profile-id", data.id);
            return apply(data);
          }
          const displayName = authUser.firstName || authUser.email?.split("@")[0] || "User";
          try {
            const createRes = await fetch("/api/profile/ensure-auth-profile", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ displayName }),
              signal,
            });
            if (createRes.ok) {
              const data = await createRes.json();
              localStorage.setItem("memnora-profile-id", data.id);
              return apply(data);
            }
          } catch (err) {
            if ((err as Error)?.name === "AbortError") return false;
            console.error("[Profile] Failed to ensure auth profile:", err);
          }
        }
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return false;
        console.error("[Profile] Failed to load auth user:", err);
      }

      if (signal.aborted) return false;
      const savedProfileId = localStorage.getItem("memnora-profile-id");
      if (savedProfileId) {
        try {
          const res = await fetch(`/api/profile/${savedProfileId}`, {
            credentials: "include",
            headers: { "x-profile-id": savedProfileId },
            signal,
          });
          if (res.ok) {
            const data = await res.json();
            return apply(data);
          } else {
            localStorage.removeItem("memnora-profile-id");
          }
        } catch (err) {
          if ((err as Error)?.name === "AbortError") return false;
          console.error("[Profile] Failed to load saved profile:", err);
        }
      }

      if (signal.aborted) return false;
      const deviceId = localStorage.getItem("memnoraDeviceId");
      if (deviceId) {
        try {
          const res = await fetch(`/api/profile/by-device/${deviceId}`, { credentials: "include", headers: { "x-device-id": deviceId }, signal });
          if (res.ok) {
            const data = await res.json();
            localStorage.setItem("memnora-profile-id", data.id);
            return apply(data);
          }
        } catch (e) {
          if ((e as Error)?.name === "AbortError") return false;
          console.warn("[Profile] Device lookup failed:", e);
        }
      }

      return false;
    }

    // Background re-attempt: after the watchdog falls back to guest mode, a slow
    // (but valid) profile load may still complete. Rather than forcing a manual
    // refresh, retry the bootstrap after the network settles and transition the
    // user to signed-in smoothly if it succeeds. A single retry isn't enough on
    // still-flaky connections, so retry a bounded number of times with
    // increasing delay (backoff) before giving up.
    const MAX_RETRY_ATTEMPTS = 3;
    const RETRY_BACKOFF_MS = [1500, 4000, 9000];
    let retryController: AbortController | null = null;
    let retryAttempts = 0;
    let onOnline: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const runBackgroundRetry = () => {
      if (resolved || cleanedUp) return;
      retryTimer = null;
      retryAttempts += 1;
      retryController = new AbortController();
      // Surface a subtle "reconnecting…" affordance while the slow profile
      // load is re-attempted in the background after the watchdog fallback.
      if (!cleanedUp) {
        setIsReconnecting(true);
        setReconnectFailed(false);
      }
      bootstrapProfile(retryController.signal)
        .then((ok) => {
          if (ok) {
            console.info("[Profile] Background retry restored signed-in session");
            return;
          }
          // Schedule another attempt with backoff until we hit the cap.
          if (!resolved && !cleanedUp && retryAttempts < MAX_RETRY_ATTEMPTS) {
            const delay = RETRY_BACKOFF_MS[Math.min(retryAttempts, RETRY_BACKOFF_MS.length - 1)];
            retryTimer = setTimeout(runBackgroundRetry, delay);
          } else if (!resolved && !cleanedUp) {
            // Exhausted all retries without restoring the session. Surface a
            // clear, dismissible "reconnect failed" prompt so the user isn't
            // left silently in guest mode.
            console.warn("[Profile] Background retry gave up; session not restored");
            setReconnectFailed(true);
          }
        })
        .catch((e) => {
          if ((e as Error)?.name !== "AbortError") console.warn("[Profile] Background retry failed:", e);
        })
        .finally(() => {
          // Only drop the reconnecting affordance once we've succeeded, given
          // up, or unmounted — not between scheduled attempts.
          const willRetryAgain = !resolved && !cleanedUp && retryAttempts < MAX_RETRY_ATTEMPTS && retryTimer !== null;
          if (!cleanedUp && !willRetryAgain) setIsReconnecting(false);
        });
    };

    const scheduleBackgroundRetry = () => {
      if (resolved || cleanedUp) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        // Wait until the connection comes back before re-attempting.
        onOnline = () => {
          if (onOnline) window.removeEventListener("online", onOnline);
          onOnline = null;
          runBackgroundRetry();
        };
        window.addEventListener("online", onOnline);
      } else {
        // Give the network a brief moment to settle before retrying.
        retryTimer = setTimeout(runBackgroundRetry, 1500);
      }
    };

    bootstrapProfile(controller.signal)
      .finally(() => {
        clearTimeout(watchdog);
        finish();
        if (watchdogFiredWhileUnresolved && !resolved) scheduleBackgroundRetry();
      });

    return () => {
      cleanedUp = true;
      clearTimeout(watchdog);
      if (retryTimer) clearTimeout(retryTimer);
      if (onOnline) window.removeEventListener("online", onOnline);
      controller.abort();
      retryController?.abort();
    };
  }, []);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "memnora-profile-id") {
        if (!e.newValue) {
          setProfileState(null);
        } else if (e.newValue !== profile?.id) {
          refreshProfile();
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [profile?.id]);

  const dismissReconnectFailed = () => setReconnectFailed(false);

  const setProfile = (newProfile: Profile | null) => {
    if (newProfile) {
      setReconnectFailed(false);
      localStorage.setItem("memnora-profile-id", newProfile.id);
      const anonId = localStorage.getItem("memnora_anon_id");
      if (anonId) {
        fetch("/api/global/merge-anon", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ anonId, userId: newProfile.id }),
        }).catch((e) => console.warn("[Profile] Anon merge failed:", e));
      }
    } else {
      localStorage.removeItem("memnora-profile-id");
    }
    setProfileState(newProfile);
  };

  const signOut = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (e) { console.warn("[Profile] Logout error:", e); }
    localStorage.removeItem("memnora-profile-id");
    localStorage.removeItem("memnora-modal-dismissed");
    setProfileState(null);
    // Session auth is a separate cache from `profile` — clear it too so
    // isAuthenticated flips immediately instead of waiting out its 5-minute
    // staleTime, and drop the "already entered" flag so "/" shows the
    // landing page again rather than staying parked on chat.
    queryClient.setQueryData(["/api/auth/user"], null);
    queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    clearEnteredInterface();
  };

  const refreshProfile = async () => {
    const savedProfileId = localStorage.getItem("memnora-profile-id");
    if (savedProfileId) {
      try {
        const res = await fetch(`/api/profile/${savedProfileId}`, {
          credentials: "include",
          headers: { "x-profile-id": savedProfileId },
        });
        if (res.ok) {
          const data = await res.json();
          setProfileState(data);
        } else {
          localStorage.removeItem("memnora-profile-id");
          setProfileState(null);
        }
      } catch (err) {
        console.error("Failed to refresh profile:", err);
      }
    }
  };

  return (
    <ProfileContext.Provider value={{ profile, isLoading, isReconnecting, reconnectFailed, dismissReconnectFailed, setProfile, refreshProfile, signOut }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within ProfileProvider");
  }
  return context;
}
